import { useEffect } from 'react';
import { useOrgChartTemplateState, ITemplate } from '@/states/organization-template.state';
import TemplateCard from './TemplateCard';
import { Plus } from 'lucide-react';
import { buildTreeFromFlatList, transformToD3Tree } from '@/utils/functions/organization.utils';

interface TemplateSelectionViewProps {
  onSelectTemplate: (template: ITemplate) => void;
  onCreateNew: () => void;
  hideTemplates?: boolean;
}

export default function TemplateSelectionView({
  onSelectTemplate,
  onCreateNew,
  hideTemplates = false,
}: Readonly<TemplateSelectionViewProps>) {
  const { templates, fetchTemplates, fetchAllTemplateDetails, fetchedTemplatesDetails, isLoading } =
    useOrgChartTemplateState();

  useEffect(() => {
    if (hideTemplates) {
      return;
    }

    fetchTemplates();
  }, [fetchTemplates, hideTemplates]);

  useEffect(() => {
    if (hideTemplates) {
      return;
    }

    if (templates.length > 0) {
      fetchAllTemplateDetails(templates.map(t => t.id));
    }
  }, [templates, fetchAllTemplateDetails, hideTemplates]);

  return (
    <div className="w-full px-4 py-6">
      {!hideTemplates && (
        <>
          <h2 className="text-xl font-bold text-slate-800 mb-6">Pilih Struktur Organisasi</h2>

          {isLoading && templates.length === 0 ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {templates.map(template => {
                const detail = fetchedTemplatesDetails[template.id];
                const treeData = detail ? transformToD3Tree(buildTreeFromFlatList(detail.nodes)) : null;

                return (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    treeData={treeData}
                    isLoading={!detail && !treeData} // Show loading if no details yet
                    onClick={() => onSelectTemplate(template)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      <button
        onClick={onCreateNew}
        className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center gap-2 text-gray-500 hover:text-primary hover:border-primary hover:bg-slate-50 transition-all group"
      >
        <div className="p-2 bg-gray-100 rounded-full group-hover:bg-primary/10 group-hover:text-primary transition-colors">
          <Plus size={20} />
        </div>
        <span className="font-semibold text-lg">Buat Struktur Organisasi Baru</span>
      </button>
    </div>
  );
}
